import { useLocation, useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect, useRef, useState } from 'react'

import { usePageVisible } from '@/hooks'
import { useIsTrialAccount } from '@/modules/account'
import { useProject } from '@/modules/projects/hooks'
import {
  hasProjectSucceeded,
  isProjectProcessing,
} from '@/modules/projects/status'
import { getProjectPath } from '@/utils'

const withoutTrailingSlash = (path: string) => path.replace(/\/$/, '')

// A trial user who waits on the case page lands on the overview, with a celebration, once the run
// ends. Only a run seen finishing here counts, so opening a case that finished earlier never pulls
// the user off a tab or celebrates again. The celebration waits until the page is visible, so a
// run that ends while the user is in another window still greets them when they come back.
export const useOpenOverviewWhenTrialFinishes = () => {
  const isTrial = useIsTrialAccount()
  const project = useProject()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const watchedRunSlug = useRef<string | null>(null)
  // #celebrate on a case URL replays the celebration and the tour after it, so both can be
  // previewed without a trial run.
  const [isPreview] = useState(() => window.location.hash === '#celebrate')
  const [isCelebrationDue, setIsCelebrationDue] = useState(isPreview)
  const isPageVisible = usePageVisible()

  const isProcessing = isProjectProcessing(project)
  const hasResults =
    hasProjectSucceeded(project) && Boolean(project?.hasTransactions)

  useEffect(() => {
    if (!project) return
    if (isProcessing) {
      watchedRunSlug.current = project.slug
      return
    }
    if (watchedRunSlug.current !== project.slug) return
    watchedRunSlug.current = null
    if (!isTrial || !hasResults) return

    setIsCelebrationDue(true)
    const overviewPath = getProjectPath(project)
    if (withoutTrailingSlash(pathname) !== overviewPath) {
      navigate({ to: overviewPath })
    }
  }, [project, isProcessing, hasResults, isTrial, pathname, navigate])

  const endCelebration = useCallback(() => setIsCelebrationDue(false), [])

  return {
    isCelebrating: isCelebrationDue && isPageVisible,
    endCelebration,
    isPreview,
  }
}
